import { useEffect, useState } from "react";
import { Play, Pause, Music4, ShoppingCart, Lock, Download, Check, Users, MessageSquare, ShieldCheck, DollarSign, Heart, Send, Upload } from "lucide-react";

/**
 * FeatureShowcase — a set of self-contained animated "walkthrough demos" that
 * play like short videos on the landing page. Each demo is a scripted sequence
 * of UI states that auto-advance on an interval, giving visitors a "watch me
 * do it" moment instead of a static feature bullet.
 *
 * The demos are CSS+state-driven, so they weigh nothing and stay in sync with
 * whatever the real product looks like.
 */

// ---------- Reusable primitives ----------

const DemoStage = ({ children, className = "", testid }) => (
  <div className={`relative rounded-2xl border border-[#1A2030] bg-[#0A0E18] overflow-hidden ${className}`} data-testid={testid}>
    <div className="absolute top-0 left-0 right-0 h-8 bg-[#050810] border-b border-[#1A2030] flex items-center px-3 gap-1.5 z-10">
      <div className="w-2.5 h-2.5 rounded-full bg-[#D14A85]/60" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#E6A85C]/60" />
      <div className="w-2.5 h-2.5 rounded-full bg-[#00E68A]/60" />
      <div className="ml-auto text-[10px] text-[#4A5260]">stardomfans.com</div>
    </div>
    <div className="pt-10 pb-6 px-6 min-h-[380px]">{children}</div>
  </div>
);

const StepDots = ({ count, active, onDot }) => (
  <div className="flex items-center justify-center gap-1.5 mt-4">
    {Array.from({ length: count }).map((_, i) => (
      <button
        key={i}
        onClick={() => onDot?.(i)}
        aria-label={`Step ${i + 1}`}
        className={`transition-all rounded-full ${i === active ? "w-8 h-1.5 bg-[#EC008C]" : "w-1.5 h-1.5 bg-[#1A2030]"}`}
      />
    ))}
  </div>
);

const useAutoAdvance = (steps, ms = 2400) => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep((s) => (s + 1) % steps), ms);
    return () => clearInterval(id);
  }, [steps, ms]);
  return [step, setStep];
};

// ---------- Demo 1 — The flagship: Upload → Auto-preview → Sell → Download ----------

const UploadSellDemo = () => {
  const [step, setStep] = useAutoAdvance(5, 2600);

  return (
    <DemoStage testid="demo-upload-sell">
      {/* Step 0 — empty form */}
      {step === 0 && (
        <div className="space-y-3 animate-in fade-in duration-500">
          <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Step 1 · Fill the form</p>
          <div className="space-y-2.5">
            <div className="h-9 rounded-lg bg-[#050810] border border-[#1A2030] px-3 flex items-center text-xs text-[#4A5260]">Midnight Frequency</div>
            <div className="grid grid-cols-2 gap-2">
              <div className="h-9 rounded-lg bg-[#050810] border border-[#1A2030] px-3 flex items-center text-xs text-[#4A5260]">Electronic</div>
              <div className="h-9 rounded-lg bg-[#050810] border border-[#1A2030] px-3 flex items-center text-xs text-[#4A5260]">128 BPM</div>
            </div>
            <div className="h-9 rounded-lg bg-[#050810] border border-[#1A2030] px-3 flex items-center text-xs text-[#4A5260]">$4.99</div>
          </div>
        </div>
      )}

      {/* Step 1 — file upload */}
      {step === 1 && (
        <div className="space-y-3 animate-in fade-in duration-500">
          <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Step 2 · Drop your track</p>
          <div className="rounded-xl border-2 border-dashed border-[#EC008C]/50 bg-[#EC008C]/[0.04] p-6 text-center">
            <Upload className="w-8 h-8 text-[#EC008C] mx-auto mb-2 animate-bounce" />
            <p className="text-white text-sm font-semibold">midnight_frequency.wav</p>
            <p className="text-[10px] text-[#8A92A0] mt-1">42.8 MB · uploading…</p>
            <div className="mt-3 h-1.5 rounded-full bg-[#1A2030] overflow-hidden">
              <div className="h-full bg-[#EC008C]" style={{ width: "68%", transition: "width 2s" }} />
            </div>
          </div>
        </div>
      )}

      {/* Step 2 — auto-preview generated */}
      {step === 2 && (
        <div className="space-y-3 animate-in fade-in duration-500">
          <p className="text-[10px] uppercase tracking-widest text-[#00E68A] font-bold">Step 3 · 30-sec preview auto-clipped ✨</p>
          <div className="rounded-xl border border-[#00E68A]/30 bg-[#00E68A]/[0.06] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-[#EC008C] flex items-center justify-center">
                <Play className="w-4 h-4 text-black ml-0.5" />
              </div>
              <div className="flex-1">
                <div className="h-2 rounded-full bg-[#1A2030] overflow-hidden mb-1.5">
                  <div className="h-full bg-gradient-to-r from-[#EC008C] to-[#FF3399]" style={{ width: "42%" }} />
                </div>
                <div className="flex justify-between text-[10px] text-[#8A92A0]"><span>0:12</span><span>0:30</span></div>
              </div>
            </div>
            <p className="text-xs text-[#00E68A] mt-3 flex items-center gap-1.5"><Check className="w-3.5 h-3.5" /> Preview public. Full track locked until purchase.</p>
          </div>
        </div>
      )}

      {/* Step 3 — buyer taps buy */}
      {step === 3 && (
        <div className="space-y-3 animate-in fade-in duration-500">
          <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Step 4 · A fan taps Buy</p>
          <div className="rounded-xl border border-[#1A2030] bg-[#050810] p-4">
            <p className="text-xs text-[#8A92A0]">Midnight Frequency</p>
            <p className="text-white font-bold text-lg">$4.99</p>
            <button className="mt-3 w-full h-10 rounded-full bg-[#EC008C] text-black font-bold text-sm flex items-center justify-center gap-2 shadow-lg shadow-[#EC008C]/40 animate-pulse">
              <ShoppingCart className="w-4 h-4" /> Buy full track — $4.99
            </button>
            <div className="mt-3 text-[10px] text-[#8A92A0] text-center flex items-center justify-center gap-1.5">
              <Lock className="w-3 h-3" /> Secure checkout
            </div>
          </div>
        </div>
      )}

      {/* Step 4 — buyer downloads, artist paid */}
      {step === 4 && (
        <div className="space-y-3 animate-in fade-in duration-500">
          <p className="text-[10px] uppercase tracking-widest text-[#00E68A] font-bold">Step 5 · Instant download + 90% payout</p>
          <div className="rounded-xl border border-[#00E68A]/30 bg-[#00E68A]/[0.06] p-4 text-center">
            <div className="w-12 h-12 rounded-full bg-[#00E68A]/20 flex items-center justify-center mx-auto mb-3">
              <Check className="w-6 h-6 text-[#00E68A]" />
            </div>
            <p className="text-white text-sm font-bold">Payment confirmed · Track unlocked</p>
            <button className="mt-3 inline-flex items-center gap-2 px-4 h-9 rounded-full bg-[#00E68A] text-black font-bold text-xs">
              <Download className="w-3.5 h-3.5" /> Download MP3 · WAV
            </button>
            <div className="mt-4 pt-3 border-t border-[#00E68A]/20 grid grid-cols-2 text-left">
              <div>
                <p className="text-[9px] uppercase tracking-widest text-[#8A92A0]">Artist earns</p>
                <p className="text-[#00E68A] font-bold">$4.49</p>
              </div>
              <div>
                <p className="text-[9px] uppercase tracking-widest text-[#8A92A0]">Platform fee</p>
                <p className="text-[#8A92A0] text-sm">$0.50</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <StepDots count={5} active={step} onDot={setStep} />
    </DemoStage>
  );
};

// ---------- Demo 2 — Community post + reactions ----------

const CommunityDemo = () => {
  const [step, setStep] = useAutoAdvance(3, 2400);
  return (
    <DemoStage testid="demo-community">
      <div className="space-y-3">
        <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Post to your fans</p>
        <div className="rounded-xl bg-[#050810] border border-[#1A2030] p-4">
          <div className="flex gap-3">
            <div className="w-8 h-8 rounded-full bg-[#EC008C]/20 flex items-center justify-center flex-shrink-0">
              <Music4 className="w-4 h-4 text-[#EC008C]" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-white font-semibold">DJ Soul</p>
              <p className="text-[10px] text-[#8A92A0]">just now</p>
              <div className="mt-2 min-h-[48px] text-sm text-white">
                {step >= 0 && <span>New track drops Friday. </span>}
                {step >= 1 && <span className="text-[#EC008C]">Preview live now →</span>}
              </div>
              {step >= 2 && (
                <div className="mt-3 flex gap-4 text-[11px] text-[#8A92A0] animate-in fade-in duration-500">
                  <span className="flex items-center gap-1"><Heart className="w-3.5 h-3.5 text-[#EC008C]" /> 128</span>
                  <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> 24</span>
                  <span className="flex items-center gap-1"><Send className="w-3.5 h-3.5" /> 12 reposts</span>
                </div>
              )}
            </div>
          </div>
        </div>
        {step >= 1 && (
          <p className="text-xs text-[#00E68A] flex items-center gap-1.5 animate-in fade-in duration-500"><Check className="w-3.5 h-3.5" /> Also shared to Community feed</p>
        )}
      </div>
      <StepDots count={3} active={step} onDot={setStep} />
    </DemoStage>
  );
};

// ---------- Demo 3 — Trust & Safety ----------

const TrustSafetyDemo = () => {
  const [step, setStep] = useAutoAdvance(3, 2400);
  return (
    <DemoStage testid="demo-trust-safety">
      <div className="space-y-3">
        <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Two taps to safer</p>
        <div className="rounded-xl bg-[#050810] border border-[#1A2030] p-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-full bg-[#1A2030] flex items-center justify-center">
              <Users className="w-4 h-4 text-[#8A92A0]" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-white font-semibold">@spamuser42</p>
              <p className="text-[10px] text-[#8A92A0]">posted something toxic</p>
            </div>
            <button className={`text-[10px] px-2.5 h-7 rounded-full border transition-all ${step >= 1 ? "bg-[#D14A85]/20 border-[#D14A85] text-[#D14A85]" : "border-[#1A2030] text-[#8A92A0]"}`}>
              {step >= 1 ? "Blocked" : "Block"}
            </button>
          </div>
        </div>
        {step >= 2 && (
          <div className="rounded-xl bg-[#00E68A]/[0.06] border border-[#00E68A]/25 p-3 flex items-start gap-2 animate-in fade-in duration-500">
            <ShieldCheck className="w-4 h-4 text-[#00E68A] mt-0.5" />
            <p className="text-xs text-[#8A92A0]">Blocked users disappear from your feed instantly — only for you. Manage the list any time.</p>
          </div>
        )}
      </div>
      <StepDots count={3} active={step} onDot={setStep} />
    </DemoStage>
  );
};

// ---------- Demo 4 — 88/12 payout (90/10 for Founders) ----------

const PayoutDemo = () => {
  const [step, setStep] = useAutoAdvance(3, 2400);
  return (
    <DemoStage testid="demo-payout">
      <div className="space-y-3">
        <p className="text-[10px] uppercase tracking-widest text-[#EC008C] font-bold">Automated payouts · 88/12 (Founders 90/10)</p>
        <div className="rounded-xl bg-[#050810] border border-[#1A2030] p-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-white text-sm font-semibold">Sale · Midnight Frequency</p>
            <p className="text-[#EC008C] font-bold text-lg">$4.99</p>
          </div>
          <div className="space-y-2">
            <div className={`flex items-center justify-between p-2.5 rounded-lg bg-[#00E68A]/[0.06] border border-[#00E68A]/25 transition-opacity ${step >= 1 ? "opacity-100" : "opacity-30"}`}>
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-[#00E68A]/20 flex items-center justify-center">
                  <DollarSign className="w-3 h-3 text-[#00E68A]" />
                </div>
                <span className="text-xs text-white">To artist (88%)</span>
              </div>
              <span className="text-[#00E68A] font-bold text-sm">$4.39</span>
            </div>
            <div className={`flex items-center justify-between p-2.5 rounded-lg bg-[#1A2030] transition-opacity ${step >= 2 ? "opacity-100" : "opacity-30"}`}>
              <span className="text-xs text-[#8A92A0]">Platform fee (12%)</span>
              <span className="text-[#8A92A0] text-sm">$0.60</span>
            </div>
          </div>
          <p className="text-[10px] text-[#6A7280] mt-2">Stripe fees netted from artist share at wire time. Founders Circle artists split 90/10.</p>
        </div>
      </div>
      <StepDots count={3} active={step} onDot={setStep} />
    </DemoStage>
  );
};

// ---------- Full showcase ----------

const FEATURES = [
  {
    id: "upload-sell",
    tag: "MAJOR FEATURE",
    title: "Upload · 30-sec preview · Sell · Instant download.",
    blurb: "The moment you upload a track, StardomFans auto-clips the first 30 seconds into a public teaser. Fans taste-test it, tap Buy, payment is confirmed, and the full file unlocks instantly — with 88% (or 90% Founders) dropped into your payout account.",
    accent: "#32D583",
    Demo: UploadSellDemo,
  },
  {
    id: "community",
    tag: "FAN COMMUNITY",
    title: "Post once. Reach your feed and the whole Community.",
    blurb: "One composer. Your fans see it, the Community feed sees it — with a single toggle. Reactions, comments, reposts, all in one place.",
    accent: "#60A5FA",
    Demo: CommunityDemo,
  },
  {
    id: "trust-safety",
    tag: "TRUST & SAFETY",
    title: "Word blocklist + one-tap block. You keep your feed yours.",
    blurb: "Bad words are refused at post-time. Unwanted users vanish from your feed with a single tap. Managed from your dashboard.",
    accent: "#A78BFA",
    Demo: TrustSafetyDemo,
  },
  {
    id: "payouts",
    tag: "AUTOMATED PAYOUTS",
    title: "Every sale splits 88 / 12 (Founders 90 / 10), automatically.",
    blurb: "Your 88% (or 90% Founders) is transferred through Stripe Connect straight to your bank on your chosen cadence — Day-11, biweekly, or monthly. Stripe processing + payout fees are netted from your share at wire time. No invoices, no chasing anyone for money.",
    accent: "#32D583",
    Demo: PayoutDemo,
  },
];

const FeatureShowcase = () => (
  <section className="py-20 sm:py-28 px-6 border-t border-[#1A2030]" data-testid="feature-showcase">
    <div className="max-w-6xl mx-auto">
      <div className="text-center mb-14 sm:mb-20">
        <span className="inline-block px-3 py-1 rounded-full bg-[#EC008C]/10 text-[#EC008C] text-[10px] font-bold uppercase tracking-widest mb-4">See it, don&apos;t read it</span>
        <h2 className="font-['Unbounded'] text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
          Every feature. <span className="italic text-[#EC008C]">Shown to you.</span>
        </h2>
        <p className="text-[#8A92A0] max-w-2xl mx-auto text-sm sm:text-base">
          No 40-page docs. Watch the app work through each core feature — end to end — right here.
        </p>
      </div>

      <div className="space-y-16 sm:space-y-24">
        {FEATURES.map((f, idx) => {
          const Demo = f.Demo;
          const reverse = idx % 2 === 1;
          return (
            <div key={f.id} className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center" data-testid={`feature-row-${f.id}`}>
              <div className={reverse ? "lg:order-2" : ""}>
                <span className="inline-block px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest mb-3 bg-[#EC008C]/10 text-[#EC008C]">{f.tag}</span>
                <h3 className="font-['Unbounded'] text-2xl sm:text-3xl font-bold text-white mb-4 leading-tight">{f.title}</h3>
                <p className="text-[#8A92A0] text-sm sm:text-base leading-relaxed">{f.blurb}</p>
              </div>
              <div className={reverse ? "lg:order-1" : ""}>
                <Demo />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

export default FeatureShowcase;
